import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SalesShell } from "@/components/crm/SalesShell";
import { EmptyState, Stat } from "@/components/crm/parts";
import { useAccounts, useContracts, useOpportunities, useQbrs } from "@/lib/crm/useCrm";
import { customerResolver, isMeetingLike, useCustomerWork } from "@/lib/crm/customer-work";
import { gbp } from "@/lib/crm/types";

export const Route = createFileRoute("/_authenticated/sales/calendar")({
  head: () => ({
    meta: [
      { title: "Sales calendar — MyPriorities" },
      {
        name: "description",
        content:
          "One calendar for expected closes, contract renewals, QBRs and customer meetings across your sales team.",
      },
      { property: "og:title", content: "Sales calendar — MyPriorities" },
      {
        property: "og:description",
        content: "See every close date, renewal, QBR and customer meeting in a single month view.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: SalesCalendarPage,
});

function SalesCalendarPage() {
  return (
    <SalesShell
      title="Sales calendar"
      subtitle="Closes, renewals, QBRs and customer meetings in one month view."
    >
      {({ orgId }) => <SalesCalendar orgId={orgId} />}
    </SalesShell>
  );
}

type EventKind = "close" | "renewal" | "qbr" | "meeting";

type CalEvent = {
  id: string;
  date: string;
  kind: EventKind;
  title: string;
  detail?: string;
};

const KIND_STYLE: Record<EventKind, { label: string; className: string }> = {
  close: { label: "Close", className: "bg-primary/15 text-primary" },
  renewal: { label: "Renewal", className: "bg-destructive/15 text-destructive" },
  qbr: { label: "QBR", className: "bg-amber-500/15 text-amber-600 dark:text-amber-400" },
  meeting: { label: "Meeting", className: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" },
};

const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function SalesCalendar({ orgId }: { orgId: string }) {
  const accounts = useAccounts(orgId);
  const opportunities = useOpportunities(orgId);
  const contracts = useContracts(orgId);
  const qbrs = useQbrs(orgId);
  const work = useCustomerWork(orgId);

  const today = new Date();
  const [cursor, setCursor] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));

  const accountName = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of accounts.data ?? []) map.set(a.id, a.name);
    return map;
  }, [accounts.data]);

  const resolver = useMemo(
    () =>
      customerResolver(
        (accounts.data ?? []).map((a) => ({ id: a.id, name: a.name })),
        work.links,
      ),
    [accounts.data, work.links],
  );

  const events = useMemo<CalEvent[]>(() => {
    const out: CalEvent[] = [];

    for (const opp of opportunities.data ?? []) {
      if (!opp.expected_close_date || opp.status !== "open") continue;
      out.push({
        id: `opp-${opp.id}`,
        date: opp.expected_close_date.slice(0, 10),
        kind: "close",
        title: opp.name,
        detail: [
          opp.account_id ? accountName.get(opp.account_id) : null,
          opp.expected_annual_value ? gbp(opp.expected_annual_value) : null,
        ]
          .filter(Boolean)
          .join(" · "),
      });
    }

    for (const contract of contracts.data ?? []) {
      if (!contract.end_date || contract.lifecycle_status !== "active") continue;
      out.push({
        id: `contract-${contract.id}`,
        date: contract.end_date.slice(0, 10),
        kind: "renewal",
        title: contract.name,
        detail: [
          contract.account_id ? accountName.get(contract.account_id) : null,
          contract.annual_value ? gbp(contract.annual_value, contract.currency) : null,
        ]
          .filter(Boolean)
          .join(" · "),
      });
    }

    for (const qbr of qbrs.data ?? []) {
      if (!qbr.next_due_on) continue;
      out.push({
        id: `qbr-${qbr.id}`,
        date: qbr.next_due_on.slice(0, 10),
        kind: "qbr",
        title: `QBR — ${accountName.get(qbr.account_id) ?? "Customer"}`,
        detail: qbr.cadence,
      });
    }

    for (const task of work.tasks) {
      if (task.done || !task.due_date || !isMeetingLike(task)) continue;
      const customers = resolver.accountsFor(task).map((a) => a.name);
      out.push({
        id: `task-${task.id}`,
        date: task.due_date.slice(0, 10),
        kind: "meeting",
        title: task.title,
        ...(customers.length ? { detail: customers.join(", ") } : {}),
      });
    }

    return out.sort((a, b) => a.date.localeCompare(b.date));
  }, [opportunities.data, contracts.data, qbrs.data, work.tasks, accountName, resolver]);

  const monthPrefix = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`;
  const monthEvents = events.filter((e) => e.date.startsWith(monthPrefix));

  const byDay = useMemo(() => {
    const map = new Map<string, CalEvent[]>();
    for (const event of monthEvents) {
      const list = map.get(event.date) ?? [];
      list.push(event);
      map.set(event.date, list);
    }
    return map;
  }, [monthEvents]);

  const cells = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const offset = (first.getDay() + 6) % 7; // Monday-first
    const days = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    const list: (Date | null)[] = Array.from({ length: offset }, () => null);
    for (let day = 1; day <= days; day += 1) {
      list.push(new Date(cursor.getFullYear(), cursor.getMonth(), day));
    }
    while (list.length % 7 !== 0) list.push(null);
    return list;
  }, [cursor]);

  const isLoading =
    accounts.isLoading || opportunities.isLoading || contracts.isLoading || qbrs.isLoading;

  const shift = (months: number) =>
    setCursor((prev) => new Date(prev.getFullYear(), prev.getMonth() + months, 1));

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading the calendar…</p>;

  const count = (kind: EventKind) => monthEvents.filter((e) => e.kind === kind).length;

  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-4">
        <Stat label="Expected closes" value={String(count("close"))} />
        <Stat label="Renewals due" value={String(count("renewal"))} />
        <Stat label="QBRs" value={String(count("qbr"))} />
        <Stat label="Customer meetings" value={String(count("meeting"))} />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-display text-lg font-semibold">
          {cursor.toLocaleDateString("en-GB", { month: "long", year: "numeric" })}
        </h2>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => shift(-1)}>
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCursor(new Date(today.getFullYear(), today.getMonth(), 1))}
          >
            This month
          </Button>
          <Button variant="outline" size="sm" onClick={() => shift(1)}>
            Next
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[44rem]">
          <div className="grid grid-cols-7 gap-1 text-[11px] uppercase tracking-wide text-muted-foreground">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
              <div key={day} className="px-2 py-1">
                {day}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((date, index) => {
              if (!date) return <div key={`pad-${index}`} className="min-h-24 rounded-md bg-muted/20" />;
              const key = iso(date);
              const dayEvents = byDay.get(key) ?? [];
              const isToday = key === iso(today);
              return (
                <div
                  key={key}
                  className={
                    "min-h-24 rounded-md border p-1.5 " +
                    (isToday ? "border-primary bg-primary/5" : "border-border bg-card")
                  }
                >
                  <p className="text-[11px] font-medium tabular-nums text-muted-foreground">
                    {date.getDate()}
                  </p>
                  <div className="mt-1 space-y-1">
                    {dayEvents.slice(0, 3).map((event) => (
                      <div
                        key={event.id}
                        className={"rounded px-1.5 py-0.5 text-[11px] leading-tight " + KIND_STYLE[event.kind].className}
                        title={event.detail ? `${event.title} — ${event.detail}` : event.title}
                      >
                        <span className="block truncate font-medium">{event.title}</span>
                        {event.detail ? (
                          <span className="block truncate opacity-80">{event.detail}</span>
                        ) : null}
                      </div>
                    ))}
                    {dayEvents.length > 3 ? (
                      <p className="text-[10px] text-muted-foreground">
                        +{dayEvents.length - 3} more
                      </p>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {(Object.keys(KIND_STYLE) as EventKind[]).map((kind) => (
          <Badge key={kind} variant="outline" className={KIND_STYLE[kind].className}>
            {KIND_STYLE[kind].label}
          </Badge>
        ))}
      </div>

      {monthEvents.length === 0 ? (
        <EmptyState
          title="Nothing in this month"
          body="Add expected close dates, contract end dates, QBR cadences or dated customer meetings to fill the calendar."
        />
      ) : null}
    </div>
  );
}
